import {keys} from 'lodash/object';
import {isEmpty, isObject, isNull} from 'lodash/lang';
import Swal from 'sweetalert2';
import {getErrorMessage} from 'utils/errors';
import {isEqual} from 'lodash';
import {SUBSCRIPTION_BILLING_METHOD} from 'configs/membership';
import urlJoin from 'url-join';
import {IAF_MAIN_URL} from 'configs/Environment';

/*
{
  animation: false,
  showConfirmButton: true,
  confirmButtonColor: '#023b89',
  confirmButtonText: 'OK',
  showCancelButton: false,
  cancelButtonColor: '#6c757d',
  cancelButtonText: 'Cancel',
  type: '',
  title: '',
  text: '',
}
 */

const optionBase = {
  animation: false,
  confirmButtonColor: '#023b89',
  confirmButtonText: 'OK',
  cancelButtonColor: '#6c757d',
  cancelButtonText: 'Cancel',
};

const swalBase = Swal.mixin(optionBase);

export const alertSuccess = (options = {}) => {
  return swalBase({
    allowOutsideClick: false,
    confirmButtonColor: '#023b89',
    confirmButtonText: 'OK',
    type: 'success',
    title: 'Success',
    ...options,
  });
};

export const alertError = (options = {}) => {
  return swalBase({
    allowOutsideClick: false,
    confirmButtonColor: '#6c757d',
    confirmButtonText: 'Close',
    type: 'error',
    title: 'Oops...',
    text: 'Something went wrong...',
    ...options,
  });
};

export const alertBase = (options = {}) => {
  if (!isObject(options)) {
    options = {};
  }

  if (isEmpty(options) || isEmpty(options.type)) {
    if (!isNull(options.type)) {
      options.type = 'success';
    }
  }

  if (options.type === 'error') {
    return alertError(options);
  } else if (options.type === 'success') {
    return alertSuccess(options);
  } else {
    return swalBase({
      allowOutsideClick: false,
      confirmButtonColor: '#023b89',
      confirmButtonText: 'OK',
      ...options,
    });
  }
};

export const alertConfirm = (options = {}) => {
  return swalBase({
    showConfirmButton: true,
    showCancelButton: true,
    type: 'warning',
    title: 'Confirm',
    text: 'Are you sure you want',
    ...options,
  });
};

export const alertByError = (error, options = {}) => {
  const {code, message, errors, status} = error;
  const {status: errorStatus} = error;

  if (errors) {
    let html = [];
    errors.forEach((error) => {
      const {field, detail} = error;

      html.push(getErrorMessage(field, detail));
    });

    options.html = html.join('<br />');
  } else if (message) {
    // default error
    if (typeof message === 'string') {
      options.text = getErrorMessage(code, message);
    }
    // validation error
    else if (typeof message === 'object') {
      let html = [];

      keys(message).forEach((key) => {
        html.push(getErrorMessage(key, message[key]));
      });

      /*values(message).forEach((item) => {
        html = html.concat(item);
      });*/

      options.html = html.join('<br />');
    }
  }

  if (errorStatus === 403 || status === 403) {
    const {redirect} = options || {};

    const footer = `<div><button id="swal-close-btn" class="swal2-cancel swal2-styled d-inline-block bg-secondary">Close</button></div>`;
    options.text = options.text
      ? options.text
      : 'You do not have permission to perform this action. If you believe this is an error, please contact support for assistance.';
    options.title = 'Forbidden Request';

    if (isEqual(code, 'invalid_email_guard_code')) {
      options.title = 'Invalid Email Guard';
    }

    return swalBase({
      type: 'error',
      showConfirmButton: false,
      showCancelButton: redirect ? true : false,
      cancelButtonText: redirect ? 'Close' : undefined,
      footer: redirect ? undefined : createSwalContent(footer),
      onClose: redirect
        ? () => window.location.replace(urlJoin(IAF_MAIN_URL, redirect))
        : undefined,
      ...options,
    });
  }

  return swalBase({
    type: 'error',
    showConfirmButton: false,
    showCancelButton: true,
    cancelButtonColor: '#6c757d',
    cancelButtonText: 'Close',
    title: 'Oops...',
    text: 'Something went wrong...',
    ...options,
  });
};

export const alertByInsuffCredits = ({
  billingMethod,
  cancelLink = '',
  okLink = '/membership/plans?redirect=certifications-list',
}) => {
  const swalOptions = {
    showCloseButton: true,
    showCancelButton: false,
    showConfirmButton: false,
    title: 'You have insufficient credits',
  };

  const isManualMethod = isEqual(
    billingMethod,
    SUBSCRIPTION_BILLING_METHOD.manual
  );

  const htmlMessage = !isManualMethod
    ? `You are trying to access data that exceeds your available credits. To access companies and their certifications, consider upgrading your plan.`
    : 'You are trying to access data that exceeds your available credits. To access companies and their certifications, please contact our support team.';

  const redirect = isManualMethod ? '/contact' : okLink;

  const html = `<div style="font-size: 15px;">
      ${htmlMessage}
    </div>`;

  const cancelButton = cancelLink
    ? `<a class="d-block d-md-inline-block btn btn-outline-primary mr-0 mr-md-2 mb-2 mb-md-0 rounded" href=${cancelLink}>Cancel</a>`
    : `<button id="swal-close-btn" class="d-block d-md-inline-block btn btn-outline-primary mr-0 mr-md-2 mb-2 mb-md-0 rounded">Cancel</button>`;

  const footer = `<div>
     ${cancelButton}
      <a class="d-block d-md-inline-block btn btn-primary rounded" href="${redirect}">Upgrade Plan</a>
    </div>`;

  alertConfirm({
    ...swalOptions,
    html: html,
    footer: createSwalContent(footer),
  });
};

function createSwalContent(innerHTML) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = innerHTML;

  setTimeout(() => {
    const closeBtn = wrapper.querySelector('#swal-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        Swal.close();
      });
    }
  }, 0);

  return wrapper;
}
